/**
 * Suggests an account code from the account name.
 *
 * The convention the import templates use is prefix-then-words: an account
 * under enterprise BSC is `BSC-EMEA`, and a sub-account of it is
 * `BSC-EMEA-CARDIOLOGY`. So the prefix is the parent account's code where there
 * is a parent and the enterprise's code where there is not, and the name
 * supplies what the prefix does not already say — nobody wants `BSC-BSC-EMEA`
 * out of "BSC EMEA".
 *
 * It is a suggestion and nothing more: the field stays editable, and the codes
 * people abbreviate by hand (`JNJ-MT-IN` for "J&J MedTech India") are ones no
 * rule would guess.
 */
export function suggestAccountCode(name: string, prefix: string): string {
  const prefixParts = tokenise(prefix);
  const nameParts = tokenise(name).filter((word) => !prefixParts.includes(word));

  const parts = [...prefixParts, ...nameParts];
  if (parts.length === 0) return '';

  // 40 is the API's limit on the column. Whole words go rather than a word
  // being cut in half, and the prefix is never what gets dropped.
  while (parts.join('-').length > 40 && parts.length > Math.max(prefixParts.length, 1)) {
    parts.pop();
  }
  return parts.join('-').slice(0, 40);
}

/**
 * Words, upper-cased, punctuation gone. Runs of single letters join up so that
 * "J&J" reads as `JJ` rather than as two separate words.
 */
function tokenise(value: string): string[] {
  const words = (value ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .split(/[^A-Z0-9]+/)
    .filter(Boolean);

  const joined: string[] = [];
  for (const word of words) {
    const previous = joined[joined.length - 1];
    if (word.length === 1 && previous && previous.length <= 2 && /^[A-Z]+$/.test(previous)) {
      joined[joined.length - 1] = previous + word;
    } else {
      joined.push(word);
    }
  }
  return joined;
}
